import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
export function SidebarAction({ actions }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const [focusIndex, setFocusIndex] = useState(-1);
    // Close on click outside
    useEffect(() => {
        if (!open)
            return;
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);
    // Keyboard navigation within menu
    useEffect(() => {
        if (!open || !menuRef.current)
            return;
        function handleKeyDown(e) {
            const items = menuRef.current?.querySelectorAll('[role="menuitem"]');
            if (!items || items.length === 0)
                return;
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setFocusIndex(prev => {
                        const next = prev < items.length - 1 ? prev + 1 : 0;
                        items[next]?.focus();
                        return next;
                    });
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setFocusIndex(prev => {
                        const next = prev > 0 ? prev - 1 : items.length - 1;
                        items[next]?.focus();
                        return next;
                    });
                    break;
                case 'Home':
                    e.preventDefault();
                    setFocusIndex(0);
                    items[0]?.focus();
                    break;
                case 'End':
                    e.preventDefault();
                    setFocusIndex(items.length - 1);
                    items[items.length - 1]?.focus();
                    break;
                case 'Escape':
                    e.preventDefault();
                    setOpen(false);
                    triggerRef.current?.focus();
                    break;
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open]);
    // Focus first menu item when opening
    useEffect(() => {
        if (open && menuRef.current) {
            const first = menuRef.current.querySelector('[role="menuitem"]');
            if (first) {
                setFocusIndex(0);
                first.focus();
            }
        }
        else {
            setFocusIndex(-1);
        }
    }, [open]);
    const handleToggle = useCallback(() => {
        setOpen(prev => !prev);
    }, []);
    const handleActionClick = useCallback((action) => {
        setOpen(false);
        action.onClick();
    }, []);
    const [triggerHovered, setTriggerHovered] = useState(false);
    return (_jsxs("div", { ref: containerRef, style: { padding: 'var(--sp-2) var(--sp-3)', position: 'relative' }, children: [_jsxs("button", { ref: triggerRef, type: "button", "data-variant": "secondary", "aria-haspopup": "true", "aria-expanded": open, onClick: handleToggle, onMouseEnter: () => setTriggerHovered(true), onMouseLeave: () => setTriggerHovered(false), style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--sp-1)',
                    width: '100%',
                    padding: '6px 12px',
                    border: '1px solid var(--border-control)',
                    borderRadius: 'var(--radius-sm)',
                    background: triggerHovered ? 'var(--surface-alt)' : 'var(--surface-elevated)',
                    color: 'var(--text)',
                    fontSize: 'var(--fs-sm)',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 32,
                }, children: [_jsx("span", { "aria-hidden": "true", style: { fontSize: 14, lineHeight: 1 }, children: "+" }), _jsx("span", { children: "New" })] }), open ? (_jsx("div", { ref: menuRef, role: "menu", "aria-label": "New actions", style: {
                    position: 'absolute',
                    top: '100%',
                    left: 'var(--sp-3)',
                    right: 'var(--sp-3)',
                    marginTop: 'var(--sp-1)',
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-md)',
                    zIndex: 30,
                    padding: 'var(--sp-1) 0',
                    overflow: 'hidden',
                }, children: actions.map((action, i) => (_jsx(MenuItemButton, { action: action, focused: focusIndex === i, onClick: () => handleActionClick(action) }, i))) })) : null] }));
}
/** Individual menu item button within the dropdown. */
function MenuItemButton({ action, focused: _focused, onClick, }) {
    const [hovered, setHovered] = useState(false);
    return (_jsxs("button", { role: "menuitem", type: "button", onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), style: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            width: '100%',
            /* 40px desktop; 44px mobile applied by consuming context */
            minHeight: 40,
            padding: '0 var(--sp-3)',
            border: 'none',
            background: hovered ? 'var(--surface-alt)' : 'transparent',
            color: 'var(--text)',
            fontSize: 'var(--fs-sm)',
            fontFamily: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
        }, children: [action.icon ? (_jsx("span", { "aria-hidden": "true", style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    color: 'var(--text-muted)',
                }, children: action.icon })) : null, _jsx("span", { children: action.label })] }));
}
//# sourceMappingURL=SidebarAction.js.map